import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { uploadToBucket } from "@/lib/uploads";
import { toast } from "sonner";
import { Plus, FileText, Paperclip, Trash2, ExternalLink } from "lucide-react";
import { AICallout } from "@/components/makai/primitives";

export const Route = createFileRoute("/teacher/planner")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Lesson planner — MAKAI" }] }),
  component: PlannerPage,
});

type Plan = {
  id: string; title: string; topic: string | null; curriculum_code: string | null;
  week_number: number | null; lesson_date: string | null; status: string;
  stream_id: string; subject_id: string;
  stream: { name: string; class: { name: string } | null } | null;
  subject: { name: string } | null;
};

type Attachment = { id: string; title: string; url: string; kind: string; storage_path: string | null };

function PlannerPage() {
  const ctx = useTeacherContext();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [filterStream, setFilterStream] = useState("");
  const [atts, setAtts] = useState<Record<string, Attachment[]>>({});

  const load = async () => {
    if (!ctx.staffId) return;
    const { data } = await supabase
      .from("lesson_plans")
      .select("id,title,topic,curriculum_code,week_number,lesson_date,status,stream_id,subject_id,stream:streams(name,class:classes(name)),subject:subjects_catalog(name)")
      .eq("teacher_id", ctx.staffId)
      .order("lesson_date", { ascending: false, nullsFirst: false });
    setPlans((data ?? []) as any);
    const ids = (data ?? []).map((p: any) => p.id);
    if (ids.length) {
      const { data: a } = await supabase.from("lesson_attachments").select("id,title,url,kind,storage_path,lesson_plan_id").in("lesson_plan_id", ids);
      const map: Record<string, Attachment[]> = {};
      for (const r of a ?? []) (map[(r as any).lesson_plan_id] ||= []).push(r as any);
      setAtts(map);
    }
  };

  useEffect(() => { if (!ctx.loading) load(); }, [ctx.loading, ctx.staffId]);

  const filtered = useMemo(() => filterStream ? plans.filter((p) => p.stream_id === filterStream) : plans, [plans, filterStream]);

  const byWeek = useMemo(() => {
    const m = new Map<string, Plan[]>();
    for (const p of filtered) {
      const k = p.week_number ? `Week ${p.week_number}` : "Unscheduled";
      (m.get(k) ?? m.set(k, []).get(k)!).push(p);
    }
    return Array.from(m.entries());
  }, [filtered]);

  return (
    <AppShell role="teacher" title="Lesson planner" subtitle="Plan lessons week by week and attach learning resources."
      action={<Button onClick={() => { setEditing(null); setOpen(true); }}><Plus className="mr-1 h-4 w-4" /> New lesson plan</Button>}>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select value={filterStream} onChange={(e) => setFilterStream(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
          <option value="">All classes</option>
          {ctx.streams.map((s) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No lesson plans yet. Create your first one to start planning the week.
        </div>
      ) : (
        <div className="space-y-6">
          {byWeek.map(([week, items]) => (
            <section key={week}>
              <h3 className="mb-2 font-display text-lg font-bold">{week}</h3>
              <div className="grid gap-3 md:grid-cols-2">
                {items.map((p) => (
                  <div key={p.id} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-display font-bold">{p.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {p.stream?.class?.name} · {p.stream?.name} · {p.subject?.name}
                          {p.lesson_date ? ` · ${new Date(p.lesson_date).toLocaleDateString()}` : ""}
                        </div>
                      </div>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.status === "published" ? "bg-primary-soft text-primary" : "bg-muted text-muted-foreground"}`}>{p.status}</span>
                    </div>
                    {p.topic && <div className="mt-2 text-sm">{p.topic}{p.curriculum_code ? ` · ${p.curriculum_code}` : ""}</div>}
                    {(atts[p.id] ?? []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {atts[p.id].map((a) => (
                          <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-xs hover:bg-primary-soft">
                            <Paperclip className="h-3 w-3" /> {a.title}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setEditing(p); setOpen(true); }}>Edit</Button>
                      <Button variant="ghost" size="sm" onClick={async () => {
                        if (!confirm("Delete this lesson plan?")) return;
                        await supabase.from("lesson_plans").delete().eq("id", p.id);
                        load();
                      }}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="mt-6">
        <AICallout
          description="Let me draft the plan, worksheet or marking guide for tomorrow."
          suggestions={[
            "Generate a lesson plan for tomorrow",
            "Create a worksheet for this topic",
            "Make a marking guide",
            "Differentiate this lesson for slower learners",
          ]}
        />
      </div>

      <PlanDialog open={open} onOpenChange={setOpen} editing={editing} ctx={ctx} onSaved={load} />
    </AppShell>
  );
}

function PlanDialog({ open, onOpenChange, editing, ctx, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ?? {
      title: "", topic: "", curriculum_code: "", week_number: "", lesson_date: "",
      objectives: "", content: "", status: "draft",
      stream_id: ctx.streams[0]?.id ?? "",
      subject_id: ctx.assignments.filter((a: any) => a.stream.id === ctx.streams[0]?.id && a.subject)[0]?.subject?.id ?? "",
    });
    setFiles([]);
  }, [open, editing, ctx.streams, ctx.assignments]);

  const subjectsForStream = ctx.assignments.filter((a: any) => a.stream.id === form.stream_id && a.subject).map((a: any) => a.subject);

  async function save() {
    if (!form.title || !form.stream_id || !form.subject_id) { toast.error("Title, class and subject are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        school_id: ctx.schoolId, teacher_id: ctx.staffId,
        week_number: form.week_number ? Number(form.week_number) : null,
        lesson_date: form.lesson_date || null,
      };
      let id = editing?.id;
      if (id) {
        const { error } = await supabase.from("lesson_plans").update(payload).eq("id", id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from("lesson_plans").insert(payload).select("id").single();
        if (error) throw error;
        id = data.id;
      }
      for (const f of files) {
        const up = await uploadToBucket("teacher-files", f, `lesson-plans/${id}`);
        await supabase.from("lesson_attachments").insert({
          lesson_plan_id: id, school_id: ctx.schoolId, kind: (up.kind === "document" ? "file" : up.kind), title: up.name,
          url: up.url, storage_path: up.path, size_bytes: up.size,
        });
      }
      toast.success("Saved");
      onOpenChange(false); onSaved();
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit lesson plan" : "New lesson plan"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Input placeholder="Title" value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <div className="grid gap-3 sm:grid-cols-2">
            <select value={form.stream_id} onChange={(e) => setForm({ ...form, stream_id: e.target.value, subject_id: "" })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">Class…</option>
              {ctx.streams.map((s: any) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
            </select>
            <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
              <option value="">Subject…</option>
              {subjectsForStream.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input placeholder="Topic" value={form.topic ?? ""} onChange={(e) => setForm({ ...form, topic: e.target.value })} />
            <Input placeholder="Curriculum code" value={form.curriculum_code ?? ""} onChange={(e) => setForm({ ...form, curriculum_code: e.target.value })} />
            <Input type="number" placeholder="Week #" value={form.week_number ?? ""} onChange={(e) => setForm({ ...form, week_number: e.target.value })} />
          </div>
          <Input type="date" value={form.lesson_date ?? ""} onChange={(e) => setForm({ ...form, lesson_date: e.target.value })} />
          <Textarea placeholder="Learning objectives" value={form.objectives ?? ""} onChange={(e) => setForm({ ...form, objectives: e.target.value })} />
          <Textarea placeholder="Lesson content / notes" rows={5} value={form.content ?? ""} onChange={(e) => setForm({ ...form, content: e.target.value })} />
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
            <option value="draft">Draft (not visible to learners)</option>
            <option value="published">Published (visible to learners)</option>
          </select>
          <div>
            <label className="text-sm font-medium">Attachments (PDFs, videos, notes)</label>
            <input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} className="mt-1 block w-full text-sm" />
            {files.length > 0 && <div className="mt-1 text-xs text-muted-foreground">{files.length} file(s) to upload</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
